import "../graphStyles.scss";
import { useEffect, useMemo, useState } from "react";
import TempDropDown from "./TempDropDown";
import { useSelector } from "react-redux";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import { Chart } from 'react-chartjs-2';;

const calcPercentage = (total, obtain) => {
  if (!total || total <= 0) return 0;
  return Math.round((obtain * 100) / total);
};

const NUDGE_WISE_FIREBASE_URL =
  "https://causalfunnel-21-dailystats.firebaseio.com/tripshock-prod/graph-nudgeWise-personaPerformance.json";

const formatDateKey = (year, month, day) => `${year}-${month}-${day}`;

const buildNudgeWiseWeekMap = (firebaseData) => {
  const result = {};

  if (!firebaseData || typeof firebaseData !== "object") return result;

  Object.entries(firebaseData).forEach(([year, monthsObj]) => {
    if (!monthsObj || typeof monthsObj !== "object") return;

    Object.entries(monthsObj).forEach(([month, daysObj]) => {
      if (!daysObj || typeof daysObj !== "object") return;

      Object.entries(daysObj).forEach(([day, dayData]) => {
        const key = formatDateKey(year, month, day);
        result[key] = Array.isArray(dayData?.data) ? dayData.data : [];
      });
    });
  });

  return result;
};

export default function CouponsWisePerformance({ weeks, data }) {
  const DBNudgeName = useSelector((state) => state.nudgeCoupon.nudgeKeyName);

  // ---------------- Coupon-wise states (unchanged table logic) ----------------
  const [currentWeek, setCurrentWeek] = useState(weeks?.[0] ?? "");
  const [currentWeekData, setCurrentWeekData] = useState(data?.[weeks?.[0]] ?? []);

  function getNudgeName(nudgeId) {
    if (!nudgeId || !Array.isArray(DBNudgeName)) return "";
    const match = DBNudgeName.find(
      (item) => item.optionValue?.toLowerCase() === nudgeId?.toLowerCase()
    );
    return match?.optionName || "";
  }

  useEffect(() => {
    if (data?.[currentWeek]) {
      setCurrentWeekData(data[currentWeek]);
    } else {
      setCurrentWeekData([]);
    }
  }, [currentWeek, data]);

  useEffect(() => {
    if (data?.[currentWeek]) {
      setCurrentWeekData(
        data[currentWeek].map((row) => ({
          ...row,
          nudgeName: getNudgeName(row.nudgeId) || row.nudgeName || ""
        }))
      );
    } else {
      setCurrentWeekData([]);
    }
  }, [DBNudgeName, currentWeek, data]);

  // ---------------- Nudge-wise states (new separate section) ----------------
  const [nudgeWiseDataMap, setNudgeWiseDataMap] = useState({});
  const [nudgeWeeks, setNudgeWeeks] = useState([]);
  const [currentNudgeWeek, setCurrentNudgeWeek] = useState("");
  const [currentNudgeWeekData, setCurrentNudgeWeekData] = useState([]);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const sessionUser = useSelector(selectLogInUsername);
  const user = sessionUser?.split('@')[0]

  useEffect(() => {
    const fetchNudgeWisePerformance = async () => {
      try {
        setNudgeLoading(true);

        const response = await fetch(`https://causalfunnel-21-dailystats.firebaseio.com/${user}/graph-nudgeWise-personaPerformance.json`);
        const json = await response.json();

        const formattedData = buildNudgeWiseWeekMap(json);
        const availableWeeks = Object.keys(formattedData).sort((a, b) => b.localeCompare(a));

        setNudgeWiseDataMap(formattedData);
        setNudgeWeeks(availableWeeks);

        if (availableWeeks.length > 0) {
          setCurrentNudgeWeek((prev) =>
            prev && formattedData[prev] ? prev : availableWeeks[0]
          );
        } else {
          setCurrentNudgeWeek("");
          setCurrentNudgeWeekData([]);
        }
      } catch (error) {
        console.error("Error fetching nudge-wise performance:", error);
        setNudgeWiseDataMap({});
        setNudgeWeeks([]);
        setCurrentNudgeWeek("");
        setCurrentNudgeWeekData([]);
      } finally {
        setNudgeLoading(false);
      }
    };

    fetchNudgeWisePerformance();
  }, []);

  useEffect(() => {
    if (currentNudgeWeek && nudgeWiseDataMap[currentNudgeWeek]) {
      setCurrentNudgeWeekData(nudgeWiseDataMap[currentNudgeWeek]);
    } else {
      setCurrentNudgeWeekData([]);
    }
  }, [currentNudgeWeek, nudgeWiseDataMap]);

  const safeNudgeWeekData = useMemo(() => {
    return Array.isArray(currentNudgeWeekData) ? currentNudgeWeekData : [];
  }, [currentNudgeWeekData]);

  const filteredNudgeWeekData = useMemo(() => {
    return safeNudgeWeekData.filter((nudge) => Number(nudge.couponShown ?? 0) > 1);
  }, [safeNudgeWeekData]);

  const safePercent = (total, value) => {
    if (!total || total <= 0) return 0;
    return Number(((value / total) * 100).toFixed(2));
  };

  const nudgeChartLabels = filteredNudgeWeekData?.map(
    (item) => item.nudgeName || item.couponCode || "Nudge"
  );


  const nudgeChartData = {
    labels: nudgeChartLabels,
    datasets: [
      // {
      //   label: "Hypotheses ATC %",
      //   data: filteredNudgeWeekData?.map((item) =>
      //     safePercent(item.eligibleUsers ?? 0, item.eligibleUsersAddToCart ?? 0)
      //   ),
      //   backgroundColor: "rgba(230, 80, 140, 1)",
      //   type: "bar",
      // },
      {
        label: "Hypotheses Without Coupon Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(
            item.eligibleButNotShownUsers ?? 0,
            item.eligibleButNotShownUsersAddToCart ?? 0
          )
        ),
        backgroundColor: "rgba(80, 180, 140, 1)",
        type: "bar",
      },
      {
        label: "Null Hypotheses Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(item.ineligibleUsers ?? 0, item.ineligibleUsersAddToCart ?? 0)
        ),
        backgroundColor: "rgba(120, 120, 120, 1)",
        type: "bar",
      },
      {
        label: "Coupon Shown Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(item.couponShown ?? 0, item.shownUsersAddToCart ?? 0)
        ),
        backgroundColor: "rgba(100, 100, 250, 1)",
        type: "bar",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Nudge",
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Add to Cart Rate (%)",
        },
      },
    },
  };

  return (
    <div>
      <div className="graph flex flex-col">
        <div className="graph__title">
          <h4>Coupons-wise performance</h4>
          <TempDropDown
            currentWeek={currentWeek}
            setCurrentWeek={setCurrentWeek}
            weeks={weeks}
          />
        </div>
        <div className="overflow-auto">
          <table className="p-1 min-w-full divide-y divide-gray-300 border rounded-lg overflow-auto max-h-[100px]">
            <thead>
              <tr className="divide-x divide-gray-300">
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900"
                >
                  Nudge name
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900"
                >
                  Coupon code
                </th>
                <th
                  scope="col"
                  className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Coupons shown
                </th>
                <th
                  scope="col"
                  className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Added to cart (via coupon shown)
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pr-0"
                >
                  Sales done (via coupon added to cart)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300 bg-white">
              {currentWeekData?.map((persona, index) => (
                <tr key={index} className="divide-x divide-gray-300">
                  <td className="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 ">
                    {persona.nudgeName}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 ">
                    {persona.couponCode}
                  </td>
                  <td className="whitespace-nowrap p-4 text-sm text-gray-600">
                    {persona.couponShown}
                  </td>

                  <td className="whitespace-nowrap py-4 pl-4 pr-4 text-sm text-gray-600 ">
                    <div className="flex justify-center gap-3 items-center">
                      <span>{persona.couponAddToCart}</span>
                      <span className="inline-flex items-center gap-x-1.5 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                        <svg
                          className="h-1.5 w-1.5 fill-yellow-500"
                          viewBox="0 0 6 6"
                          aria-hidden="true"
                        >
                          <circle cx={3} cy={3} r={3} />
                        </svg>
                        {calcPercentage(persona.couponShown, persona.couponAddToCart)}%
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-4 text-sm text-gray-600 ">
                    <div className="flex justify-center gap-3 items-center">
                      <span>{persona.couponSalesDone}</span>
                      <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        <svg
                          className="h-1.5 w-1.5 fill-green-500"
                          viewBox="0 0 6 6"
                          aria-hidden="true"
                        >
                          <circle cx={3} cy={3} r={3} />
                        </svg>
                        {calcPercentage(persona.couponAddToCart, persona.couponSalesDone)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!nudgeLoading && filteredNudgeWeekData?.length > 0 && (
        <>
          <div className="graph flex flex-col">
            <div className="graph__title">
              <h4>A/B test performance report</h4>
              <TempDropDown
                currentWeek={currentNudgeWeek}
                setCurrentWeek={setCurrentNudgeWeek}
                weeks={nudgeWeeks}
              />
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      rowSpan="2"
                      className="border border-gray-200 px-4 py-3 text-left align-middle font-semibold text-gray-900"
                    >
                      Nudge name
                    </th>
                    <th
                      rowSpan="2"
                      className="border border-gray-200 px-4 py-3 text-left align-middle font-semibold text-gray-900"
                    >
                      Coupon code
                    </th>

                    {[
                      // "Hypotheses",
                      "Hypotheses Without Coupon",
                      "Null Hypotheses",
                      "Coupon Shown",
                    ].map((head) => (
                      <th
                        key={head}
                        colSpan="2"
                        className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-900"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>

                  <tr>
                    {[
                      "User",
                      "Add to cart",
                      "User",
                      "Add to cart",
                      "User",
                      "Add to cart",
                      // "User",
                      // "Add to cart",
                    ].map((head, index) => (
                      <th
                        key={index}
                        className="border border-gray-200 px-4 py-2 text-center text-xs font-semibold text-gray-700"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {filteredNudgeWeekData.map((nudge, index) => {
                    const hypothesesUsers = nudge.eligibleUsers ?? 0;
                    const hypothesesATC = nudge.eligibleUsersAddToCart ?? 0;

                    const withoutCouponUsers =
                      (nudge.eligibleUsers ?? 0) - (nudge.couponShown ?? 0);
                    const withoutCouponATC =
                      (nudge.eligibleUsersAddToCart ?? 0) -
                      (nudge.shownUsersAddToCart ?? 0);

                    const nullUsers = nudge.ineligibleUsers ?? 0;
                    const nullATC = nudge.ineligibleUsersAddToCart ?? 0;

                    const couponShownUsers = nudge.couponShown ?? 0;
                    const couponShownATC = nudge.shownUsersAddToCart ?? 0;

                    const ATCCell = ({ users, atc }) => (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-700">{atc}</span>
                        <span className="inline-flex min-w-[44px] justify-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                          {calcPercentage(users, atc)}%
                        </span>
                      </div>
                    );

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm font-medium text-gray-900">
                          {nudge.nudgeName || ""}
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm font-medium text-gray-900">
                          {nudge.couponCode || ""}
                        </td>

                        {/* <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {hypothesesUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={hypothesesUsers} atc={hypothesesATC} />
                        </td> */}

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {withoutCouponUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={withoutCouponUsers} atc={withoutCouponATC} />
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {nullUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={nullUsers} atc={nullATC} />
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {couponShownUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={couponShownUsers} atc={couponShownATC} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="graph flex flex-col col-span-2">
            <div className="graph__title">
              <h4>Nudge Add to Cart Rate Comparison</h4>
              <TempDropDown
                currentWeek={currentNudgeWeek}
                setCurrentWeek={setCurrentNudgeWeek}
                weeks={nudgeWeeks}
              />
            </div>

            <Chart
              className="chart"
              id="nudge-conversion-chart"
              options={options}
              data={nudgeChartData}
            />
          </div>
        </>
      )}

    </div>
  );
}



// const NudgeConversionComparison = ({ weeks, graphData }) => {
//   const [currentWeek, setCurrentWeek] = useState(weeks[0] ?? "");
//   const [currentWeekData, setCurrentWeekData] = useState(graphData[currentWeek] ?? []);

//   useEffect(() => {
//     setCurrentWeekData(graphData[currentWeek] ?? []);
//   }, [currentWeek, graphData]);

//   const safePercent = (total, value) => {
//     if (!total || total <= 0) return 0;
//     return Number(((value / total) * 100).toFixed(2));
//   };

//   const labels = currentWeekData?.map((item) => item.nudgeName || item.couponCode || "Nudge");

//   const data = {
//     labels,
//     datasets: [
//       {
//         label: "Hypotheses ATC %",
//         data: currentWeekData?.map((item) =>
//           safePercent(item.eligibleUsers, item.eligibleUsersAddToCart)
//         ),
//         backgroundColor: "rgba(80, 180, 140, 1)",
//         type: "bar",
//       },
//       {
//         label: "Hypotheses Without Coupon ATC %",
//         data: currentWeekData?.map((item) =>
//           safePercent(
//             item.eligibleButNotShownUsers,
//             item.eligibleButNotShownUsersAddToCart
//           )
//         ),
//         backgroundColor: "rgba(230, 170, 80, 1)",
//         type: "bar",
//       },
//       {
//         label: "Null Hypotheses ATC %",
//         data: currentWeekData?.map((item) =>
//           safePercent(item.ineligibleUsers, item.ineligibleUsersAddToCart)
//         ),
//         backgroundColor: "rgba(120, 120, 120, 1)",
//         type: "bar",
//       },
//       {
//         label: "Coupon Shown ATC %",
//         data: currentWeekData?.map((item) =>
//           safePercent(item.couponShown, item.shownUsersAddToCart)
//         ),
//         backgroundColor: "rgba(100, 100, 250, 1)",
//         type: "bar",
//       },
//     ],
//   };

//   const options = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: {
//         position: "right",
//       },
//       tooltip: {
//         callbacks: {
//           label: function (context) {
//             return `${context.dataset.label}: ${context.raw}%`;
//           },
//         },
//       },
//     },
//     scales: {
//       x: {
//         title: {
//           display: true,
//           text: "Nudge",
//         },
//         grid: {
//           display: false,
//         },
//       },
//       y: {
//         beginAtZero: true,
//         title: {
//           display: true,
//           text: "Add to Cart Rate (%)",
//         },
//       },
//     },
//   };

//   return (
//     <div className="graph flex flex-col col-span-2">
//       <div className="graph__title">
//         <h4>Nudge Add to Cart Rate Comparison</h4>
//         <TempDropDown
//           currentWeek={currentWeek}
//           setCurrentWeek={setCurrentWeek}
//           weeks={weeks}
//         />
//       </div>

//       <Chart className="chart" id="nudge-conversion-chart" options={options} data={data} />
//     </div>
//   );
// };